<template>
    <div class="calendar">
        <v-sheet class="calendar-header">
            <v-toolbar class="calendar-header-toolbar">
                <v-btn class="calendar-header-toolbar-today mr-2" color="grey darken-2" outlined @click="setToday">
                    {{ t('calendar::label.today') }}
                </v-btn>
                <v-btn color="grey darken-2 calendar-header-toolbar-prev" fab small text @click="prev">
                    <v-icon small>mdi-arrow-left</v-icon>
                </v-btn>
                <v-toolbar-title class="calendar-header-toolbar-title">
                    {{ title }}
                </v-toolbar-title>
                <v-btn class="calendar-header-toolbar-next" color="grey darken-2" fab small text @click="next">
                    <v-icon small>mdi-arrow-right</v-icon>
                </v-btn>
                <v-menu v-if="!monthOnly" bottom class="calendar-header-toolbar-menu" right>
                    <template #activator="{ on }">
                        <v-btn :disabled="monthOnly" class="ml-2" color="grey darken-2" outlined v-on="on">
                            <span>{{ typeToLabel[calendarType] }}</span>
                            <v-icon right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item v-if="!monthOnly" @click="calendarType = 'day'">
                            <v-list-item-title>{{ t('calendar::label.day') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="!monthOnly" @click="calendarType = 'week'">
                            <v-list-item-title>{{ t('calendar::label.week') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="calendarType = 'month'">
                            <v-list-item-title>{{ t('calendar::label.month') }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-toolbar>
        </v-sheet>
        <v-sheet :height="height" class="v-calendar-wrap">
            <v-skeleton-loader
                height="300"
                type="image"
                :style="isLoading && disableNonEventDays ? dBlock : dNone"
            ></v-skeleton-loader>
            <v-calendar
                ref="calendar"
                v-model="calendarModel"
                :day-format="(d) => moment(d.date).format('DD')"
                :disabled-dates="allowedDates"
                :event-color="getEventColor"
                :events="events"
                :locale="locale"
                :month-format="(d) => _.upperFirst(moment(d.date).format('MMM'))"
                :now="today"
                :type="calendarType"
                :weekdays="weekdays"
                class="calendar-wrap"
                v-bind="$attrs"
                :style="isLoading && disableNonEventDays ? dNone : dFlex"
                @change="updateRange"
                @click:date="viewDay"
                @click:more="viewDay"
                v-on="$listeners"
            >
                <template #event="{ event }">
                    <div class="pl-1">
                        <strong>{{ moment(event.start).format('HH:mm') }}</strong>
                        {{ event.name }}
                    </div>
                </template>
                <template v-for="(_, slot) of $scopedSlots" #[slot]="scope">
                    <slot :name="slot" v-bind="scope"></slot>
                </template>
            </v-calendar>
        </v-sheet>
    </div>
</template>

<script>
export default {
    name: 'CalendarComponent',
    props: {
        showToday: {
            type: Boolean,
            required: false,
            default: false,
        },
        showMenu: {
            type: Boolean,
            required: false,
            default: false,
        },
        showYearInTitle: {
            type: Boolean,
            required: false,
            default: true,
        },
        events: {
            type: Array,
            required: false,
            default: function () {
                return [];
            },
        },
        height: {
            type: String,
            required: false,
            default: '600',
        },
        locale: {
            type: String,
            required: false,
            default: 'en-US',
        },
        type: {
            type: String,
            default: 'month',
        },
        startTime: {
            type: String,
            default: '8:00',
        },
        weekdays: {
            type: Array,
            default: function () {
                return [1, 2, 3, 4, 5, 6, 0];
            },
        },
        monthOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        removeEvents: {
            type: Boolean,
            required: false,
            default: false,
        },
        disableNonEventDays: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            calendarModel: '',
            today: moment().format('YYYY-MM-DD'),
            typeToLabel: {
                month: t('calendar::label.month'),
                week: t('calendar::label.week'),
                day: t('calendar::label.day'),
            },
            start: null,
            end: null,
            calendarType: _.clone(this.type),
            isLoading: false,
            dNone: {
                display: 'none',
            },
            dBlock: {
                display: 'block',
            },
            dFlex: {
                display: 'flex',
            },
        };
    },
    computed: {
        allowedDates() {
            window.dispatchEvent(new Event('resize'));

            if (this.calendarType === 'day' || !this.disableNonEventDays) {
                return false;
            }

            const days = document.querySelectorAll('.v-calendar-weekly__day, .v-calendar-daily_head-day');
            this.disableAllDays(days);

            for (const event of this.events) {
                const eventDayInMonth = moment(event.start).format('DD');
                const eventMonth = moment(event.start).format('MMM');

                const dateWithEvents = Array.from(days).filter((x) => {
                    if (this.removeEvents) {
                        x.classList.add('no-events');
                    }

                    if (x.className.includes('v-outside')) {
                        return false;
                    }

                    return (
                        x.querySelector('.v-btn__content').innerHTML === eventDayInMonth ||
                        x.querySelector('.v-btn__content').innerHTML === eventMonth + ' ' + eventDayInMonth
                    );
                });

                if (dateWithEvents.length) {
                    this.setDayToActive(dateWithEvents[0]);
                }
            }

            return true;
        },
        title() {
            const { start, end } = this;
            if (!start || !end) {
                return '';
            }

            const startMonth = _.upperFirst(moment(start.date).format('MMMM'));
            const endMonth = _.upperFirst(moment(end.date).format('MMMM'));
            const suffixMonth = startMonth === endMonth ? '' : endMonth;

            const startYear = start.year;
            const endYear = end.year;
            const suffixYear = startYear === endYear ? '' : startYear;

            const startDay = moment(start.date).format('DD');
            const endDay = moment(end.date).format('DD');

            switch (this.calendarType) {
                case 'month':
                    return this.showYearInTitle ? `${startMonth} ${startYear}` : `${startMonth}`;
                case 'week':
                    return `${startMonth} ${startDay} ${suffixYear} - ${suffixMonth} ${endDay} ${endYear}`;
                case 'day':
                    return `${startMonth} ${startDay} ${startYear}`;
            }
            return '';
        },
    },
    mounted() {
        this.$refs.calendar.checkChange();
        this.$refs.calendar.scrollToTime(this.startTime);
    },
    methods: {
        disableAllDays(days) {
            if (this.disableNonEventDays) {
                days.forEach((x) => {
                    x.querySelector('.v-btn').setAttribute('disabled', 'disabled');
                    x.setAttribute('disabled', 'disabled');
                    x.classList.add('disabled-calendar-day');
                });
            }
        },
        setDayToActive(day) {
            day.classList.remove('disabled-calendar-day');
            day.classList.add('active-calendar-day');

            if (!this.monthOnly) {
                day.querySelector('.v-btn').removeAttribute('disabled', 'disabled');
            }
        },
        prev() {
            this.$refs.calendar.prev();
        },
        next() {
            this.$refs.calendar.next();
        },
        setToday() {
            this.calendarModel = this.today;
        },
        updateRange({ start, end }) {
            this.start = start;
            this.end = end;

            if (this.calendarType !== 'day' && this.disableNonEventDays) {
                this.isLoading = true;
            }

            this.$emit('change', { start: start, end: end });

            this.isLoading = false;
        },
        viewDay({ date }) {
            if (this.monthOnly) {
                this.calendarModel = date;
                return;
            }

            this.calendarModel = date;
            this.calendarType = 'day';
        },
        getEventColor(event) {
            return event.color;
        },
    },
};
</script>
